import { TodayRounded } from '@mui/icons-material';
import {
    alpha,
    FormControl,
    InputAdornment,
    InputLabel,
    SxProps,
    TextField,
    useTheme,
} from '@mui/material';
import { TZDate } from '@repo/tzdate';
import { createRef, useMemo } from 'react';
import { capitalize } from '@repo/common-utils/TextUtils';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';

type IProps = {
    labelColor: string | undefined;
    label: string;
    showError: boolean;
    required: boolean;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLInputElement | HTMLDivElement | null>>;
    dateValue: TZDate | null;
    variant?: 'filled' | 'outlined';
    sx?: SxProps;
    textColor?: string;
    backgroundColor?: string;
};

export default function DateTimePickerInput(props: IProps) {
    const {
        labelColor,
        label,
        showError,
        required,
        setAnchorEl,
        dateValue,
        variant = 'filled',
        sx,
        textColor,
        backgroundColor,
    } = props;
    const theme = useTheme();
    const fieldRef = createRef<HTMLDivElement>();

    const input = useMemo(() => {
        if (dateValue === null) {
            return '';
        }

        return dateValue.forceLocalTz().format('DD.MM.YYYY HH:mm');
    }, [dateValue]);

    const handleClick = () => {
        setAnchorEl(fieldRef.current);
    };

    return (
        <FormControl sx={{ ...sx, width: '100%' }} variant="standard">
            <InputLabel
                shrink
                required={required}
                sx={{
                    ...TextFieldStyleUtils.positionInputLabelAboveField,
                    ...(labelColor ? { color: `${labelColor} !important` } : {}),
                }}
            >
                {capitalize(label)}
            </InputLabel>
            <TextField
                value={input}
                error={showError}
                variant={variant}
                onClick={handleClick}
                InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <TodayRounded
                                sx={{
                                    color: textColor ? textColor : 'primary.main',
                                }}
                            />
                        </InputAdornment>
                    ),
                    sx: {
                        backgroundColor: backgroundColor
                            ? backgroundColor
                            : theme.palette.common.white,
                        '&:focus, &:hover, &.Mui-focused': {
                            backgroundColor: backgroundColor
                                ? alpha(backgroundColor as string, 0.8)
                                : alpha(theme.palette.common.white, 0.9),
                        },
                        ...(textColor ? { color: textColor } : {}),
                    },
                }}
                ref={fieldRef}
                sx={{
                    border: showError ? `1px solid ${theme.palette.error.main}` : 'none',
                    borderRadius: 1,
                }}
                fullWidth
                size="small"
                margin="dense"
            />
        </FormControl>
    );
}
